import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:4000';

const BadgeContext = createContext({ badges: { notifs: 0, messages: 0, nominations: 0, enrRequests: 0 }, clearNotifs: () => {}, clearMessages: () => {}, refetch: () => {} });

export function BadgeProvider({ children }) {
  const { currentUser } = useAuth();
  const [badges, setBadges] = useState({ notifs: 0, messages: 0, nominations: 0, enrRequests: 0 });

  const fetchBadges = useCallback(async () => {
    if (!currentUser) { setBadges({ notifs: 0, messages: 0, nominations: 0, enrRequests: 0 }); return; }
    try {
      const isAdmin  = currentUser.role === 'admin';
      const isTutor  = currentUser.role === 'tutor';
      const [notifRes, msgRes, nomRes, enrRes] = await Promise.all([
        api.get('/notifications/unread-count').catch(() => ({ data: { count: 0 } })),
        api.get('/messages/conversations').catch(() => ({ data: { conversations: [] } })),
        isAdmin ? api.get('/nominations?status=pending').catch(() => ({ data: { nominations: [] } })) : Promise.resolve({ data: { nominations: [] } }),
        (isAdmin || isTutor) ? api.get('/enrollment-requests?status=pending').catch(() => ({ data: { requests: [] } })) : Promise.resolve({ data: { requests: [] } }),
      ]);
      const notifs      = notifRes.data.count || 0;
      const messages    = (msgRes.data.conversations || []).reduce((s, c) => s + (c.unreadCount || 0), 0);
      const nominations = (nomRes.data.nominations  || []).length;
      const enrRequests = (enrRes.data.requests     || []).filter(r => r.status === 'pending').length;
      setBadges({ notifs, messages, nominations, enrRequests });
    } catch {}
  }, [currentUser]);

  useEffect(() => {
    fetchBadges();
    const id = setInterval(fetchBadges, 30000);
    return () => clearInterval(id);
  }, [fetchBadges]);

  // Live updates — the backend emits `notification` to user:<id> and
  // `new_message_preview` on new DMs, so the bell/badges update instantly
  // instead of waiting up to 30s for the next poll.
  useEffect(() => {
    if (!currentUser) return;
    const token = localStorage.getItem('tt_token');
    const socket = io(SOCKET_URL, { auth: { token } });
    socket.on('notification',         () => setBadges(b => ({ ...b, notifs: b.notifs + 1 })));
    socket.on('new_message_preview',  () => setBadges(b => ({ ...b, messages: b.messages + 1 })));
    return () => socket.disconnect();
  }, [currentUser?._id]); // eslint-disable-line

  const clearNotifs   = useCallback(() => setBadges(b => ({ ...b, notifs:   0 })), []);
  const clearMessages = useCallback(() => setBadges(b => ({ ...b, messages: 0 })), []);
  const addNotif      = useCallback(() => setBadges(b => ({ ...b, notifs:   b.notifs + 1 })), []);

  return (
    <BadgeContext.Provider value={{ badges, clearNotifs, clearMessages, addNotif, refetch: fetchBadges }}>
      {children}
    </BadgeContext.Provider>
  );
}

export const useBadges = () => useContext(BadgeContext);
